/**
 * COSO Compliance Page (EX8)
 * 
 * Main dashboard for COSO 2013 framework compliance
 * Features:
 * - Two tabs: COSO Compliance and Documents
 * - 5 collapsible sections for COSO components
 * - 17 clickable principle cards
 * - Navigation links to related exhibits
 * - Narrative entry side panel with RTE
 * - API integration for fetching and updating notes
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, Tabs, message } from 'antd';
import { 
  DownOutlined, 
  RightOutlined, 
  MessageOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import ChangeLogButton from '../changeLog/ChangeLogButton';
import ExportButton from '../../../components/common/ExportButton';
import ExportPanel from '../../../components/common/ExportPanel';
import AcknowledgmentIcon from '../../../assets/icons/ic-acknowledgment.svg?react';
import DocumentIcon from '../../../assets/icons/ic-document.svg?react';
import { RootState, AppDispatch } from '../../../store';
import { setNotes, setLoadingNotes } from '../../../store/slices/cosoComplianceSlice';
import { getCOSOComplianceNotes } from '../../../services/cosoComplianceService';
import { 
  COSO_COMPONENTS, 
  COSO_PRINCIPLES,
  COSOPrinciple,
  COSOComponent,
} from './cosoComplianceData';
import COSOPrincipleDrawer from './COSOPrincipleDrawer';
import COSODocumentsTab from './COSODocumentsTab';

const { Title } = Typography;

// ========== STYLED COMPONENTS ==========

const PageContainer = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PageTitle = styled(Title)`
  margin: 0 !important;
  font-family: Inter !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 100% !important;
  letter-spacing: 0.005em !important;
  color: #1F2937 !important;
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px;
  }

  .ant-tabs-tab {
    padding: 12px 16px;
    
    .ant-tabs-tab-btn {
      font-family: Inter;
      font-weight: 500;
      font-size: 14px;
      color: #6B7280;
    }

    svg path,
    svg circle {
      stroke: #6B7280;
      transition: stroke 0.3s;
    }

    &.ant-tabs-tab-active {
      .ant-tabs-tab-btn {
        color: #2563EB;
      }

      svg path,
      svg circle {
        stroke: #2563EB;
      }
    }

    &:hover {
      .ant-tabs-tab-btn {
        color: #2563EB;
      }

      svg path,
      svg circle {
        stroke: #2563EB;
      }
    }
  }

  .ant-tabs-ink-bar {
    background: #2563EB;
  }
`;

const TabLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ComponentSection = styled.div<{ $bgColor: string }>`
  margin-bottom: 24px;
  background: ${props => props.$bgColor};
  border-radius: 8px;
  overflow: hidden;
`;

const ComponentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  cursor: pointer;
  user-select: none;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.85;
  }
`;

const CollapseIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #374151;
  transition: transform 0.2s;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const ComponentTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 15px;
  color: #1F2937;
  flex: 1;
`;

const PrinciplesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0 16px 16px 16px;
`;

const PrincipleCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  border-left: 4px solid transparent;
  min-height: 130px;
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 180px;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
    border-left-color: #2563EB;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const PrincipleNumber = styled.div`
  font-family: Inter;
  font-weight: 700;
  font-size: 24px;
  color: #1F2937;
  line-height: 1;
`;

const CommentBadge = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: #6B7280;
  
  .badge-count {
    position: absolute;
    top: -4px;
    right: -4px;
    min-width: 16px;
    height: 16px;
    background: #2563EB;
    border-radius: 50%;
    font-size: 10px;
    font-weight: 600;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
  }
`;

const PrincipleDescription = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  color: #4B5563;
  line-height: 1.5;
  flex: 1;
  margin-bottom: 12px;
`;

const NavigationLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  color: #2563EB;
  text-decoration: none;
  margin-top: auto;
  
  &:hover {
    color: #1D4ED8;
    text-decoration: underline;
  }
  
  &::after {
    content: '›';
    font-size: 16px;
    line-height: 1;
  }
`;

// ========== COMPONENT: PrincipleCardItem ==========

interface PrincipleCardItemProps {
  principle: COSOPrinciple;
  hasContent: boolean;
  onClick: () => void;
  onNavigate: (path: string, state?: Record<string, unknown>) => void;
}

const PrincipleCardItem: React.FC<PrincipleCardItemProps> = ({
  principle,
  hasContent,
  onClick,
  onNavigate,
}) => {
  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent triggering if clicking on navigation link
    if ((e.target as HTMLElement).closest('a')) {
      return;
    }
    onClick();
  };

  const handleNavLinkClick = (e: React.MouseEvent, path: string, state?: Record<string, unknown>) => {
    e.preventDefault();
    e.stopPropagation();
    onNavigate(path, state);
  };

  return (
    <PrincipleCard onClick={handleCardClick}>
      <CardHeader>
        <PrincipleNumber>
          {principle.number < 10 ? `0${principle.number}` : principle.number}
        </PrincipleNumber>
        {hasContent && (
          <CommentBadge>
            <MessageOutlined style={{ fontSize: 18 }} />
          </CommentBadge>
        )}
      </CardHeader>
      
      <PrincipleDescription>
        {principle.description}
      </PrincipleDescription>
      
      {principle.navigationLink && (
        <NavigationLink
          href={principle.navigationLink.path}
          onClick={(e) => handleNavLinkClick(e, principle.navigationLink!.path, principle.navigationLink!.state)}
        >
          {principle.navigationLink.label}
        </NavigationLink>
      )}
    </PrincipleCard>
  );
};

// ========== COMPONENT: COSOComponentSection ==========

interface COSOComponentSectionProps {
  component: COSOComponent;
  principles: COSOPrinciple[];
  principlesWithContent: Set<number>;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onPrincipleClick: (principle: COSOPrinciple) => void;
  onNavigate: (path: string, state?: Record<string, unknown>) => void;
}

const COSOComponentSection: React.FC<COSOComponentSectionProps> = ({
  component,
  principles,
  principlesWithContent,
  isCollapsed,
  onToggleCollapse,
  onPrincipleClick,
  onNavigate,
}) => {
  return (
    <ComponentSection $bgColor={component.bgColor}>
      <ComponentHeader onClick={onToggleCollapse}>
        <CollapseIcon>
          {isCollapsed ? <RightOutlined /> : <DownOutlined />}
        </CollapseIcon>
        <ComponentTitle>{component.name}</ComponentTitle>
      </ComponentHeader>
      
      {!isCollapsed && (
        <PrinciplesGrid>
          {principles.map(principle => (
            <PrincipleCardItem
              key={principle.id}
              principle={principle}
              hasContent={principlesWithContent.has(principle.number)}
              onClick={() => onPrincipleClick(principle)}
              onNavigate={onNavigate}
            />
          ))}
        </PrinciplesGrid>
      )}
    </ComponentSection>
  );
};

// ========== MAIN COMPONENT ==========

// Valid tab keys
const VALID_TABS = ['compliance', 'documents'] as const;
type TabKey = typeof VALID_TABS[number];

const COSOCompliancePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [selectedPrinciple, setSelectedPrinciple] = useState<COSOPrinciple | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isExportPanelOpen, setIsExportPanelOpen] = useState(false);
  
  // Get current tab from URL or default to 'compliance'
  const currentTab = useMemo(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && VALID_TABS.includes(tabParam as TabKey)) {
      return tabParam;
    }
    return 'compliance';
  }, [searchParams]);
  
  // Handle tab change - update URL
  const handleTabChange = useCallback((key: string) => {
    setSearchParams({ tab: key }, { replace: true });
  }, [setSearchParams]);
  
  // Get data from Redux
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  const { notes, notesLastFetchedReportId } = useSelector(
    (state: RootState) => state.cosoCompliance
  );
  
  // Create a Set of principle numbers that have content
  const principlesWithContent = new Set(
    notes.filter(n => n.content && n.content.trim() !== '').map(n => n.principleNumber)
  );

  // Fetch notes when component mounts or report changes
  useEffect(() => {
    const fetchNotes = async () => {
      if (!selectedReport?.annualReportId) {
        return;
      }
      
      // Check Redux cache - skip fetch if data already loaded for this report
      if (notesLastFetchedReportId === selectedReport.annualReportId) {
        // console.log('⏭️ COSOCompliancePage: Using cached data for:', selectedReport.annualReportId);
        return;
      }
      
      dispatch(setLoadingNotes(true));
      
      try {
        // console.log('📝 COSOCompliancePage: Fetching notes...');
        const response = await getCOSOComplianceNotes(selectedReport.annualReportId);
        
        dispatch(setNotes({
          notes: response.data,
          reportId: selectedReport.annualReportId,
        }));
        
        // console.log('✅ COSOCompliancePage: Notes loaded:', response.data.length);
      } catch (error: any) {
        console.error('Error fetching notes:', error);
        message.error(error?.message || 'Failed to load COSO compliance notes');
      } finally {
        dispatch(setLoadingNotes(false));
      }
    };
    
    fetchNotes();
  }, [selectedReport?.annualReportId, notesLastFetchedReportId, dispatch]);

  const toggleSection = useCallback((sectionId: string) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  }, []);

  const handlePrincipleClick = useCallback((principle: COSOPrinciple) => {
    setSelectedPrinciple(principle);
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setSelectedPrinciple(null);
  }, []);

  const handleNavigate = useCallback((path: string, state?: Record<string, unknown>) => {
    navigate(path, { state });
  }, [navigate]);

  // Get principles for each component (from static data)
  const getPrinciplesForComponent = useCallback((componentId: string) => {
    return COSO_PRINCIPLES.filter(p => p.componentId === componentId);
  }, []);
  
  // Export handlers
  const handleOpenExportPanel = useCallback(() => {
    setIsExportPanelOpen(true);
  }, []);
  
  const handleCloseExportPanel = useCallback(() => {
    setIsExportPanelOpen(false);
  }, []);
  
  // Prepare export data - combine principles with their content from notes
  // Note: HTML to plain text conversion is handled automatically by exportUtils
  const exportData = useMemo(() => {
    return COSO_PRINCIPLES.map(principle => {
      // Find the content for this principle from notes
      const note = notes.find(n => n.principleNumber === principle.number);
      const content = note?.content?.trim() || '-';
      
      return {
        name: `COSO Principle ${principle.number} - Compliance Response`,
        description: principle.description,
        content,
      };
    });
  }, [notes]);
  
  // Export columns configuration
  const exportColumns = useMemo(() => [
    { key: 'name', title: 'Name', width: 35 },
    { key: 'description', title: 'Description', width: 30 },
    { key: 'content', title: 'Content', width: 50 },
  ], []);

  const tabItems = [
    {
      key: 'compliance',
      label: (
        <TabLabel>
          <AcknowledgmentIcon />
          COSO Compliance
        </TabLabel>
      ),
      children: (
        <>
          {COSO_COMPONENTS.map(component => (
            <COSOComponentSection
              key={component.id}
              component={component}
              principles={getPrinciplesForComponent(component.id)}
              principlesWithContent={principlesWithContent}
              isCollapsed={collapsedSections.has(component.id)}
              onToggleCollapse={() => toggleSection(component.id)}
              onPrincipleClick={handlePrincipleClick}
              onNavigate={handleNavigate}
            />
          ))}
        </>
      ),
    },
    {
      key: 'documents',
      label: (
        <TabLabel>
          <DocumentIcon />
          Documents
        </TabLabel>
      ),
      children: <COSODocumentsTab />,
    },
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle level={2}>COSO Compliance</PageTitle>
        <HeaderActions>
          {currentTab === 'compliance' && (
            <ExportButton 
              onClick={handleOpenExportPanel}
              title="Export all principles"
            />
          )}
          {selectedReport && isAdmin && (
            <ChangeLogButton
              actionType="compliance"
              annualReportId={selectedReport.annualReportId}
            />
          )}
        </HeaderActions>
      </PageHeader>

      <StyledTabs
        activeKey={currentTab}
        onChange={handleTabChange}
        items={tabItems}
      />

      {/* Narrative Entry Drawer */}
      <COSOPrincipleDrawer
        open={drawerOpen}
        principle={selectedPrinciple}
        onClose={handleDrawerClose}
      />
      
      {/* Export Panel */}
      <ExportPanel
        open={isExportPanelOpen}
        onClose={handleCloseExportPanel}
        data={exportData}
        columns={exportColumns}
        fileName="COSO_Compliance_Report"
        title="COSO Compliance - All Principles"
        sheetName="COSO Principles"
      />
    </PageContainer>
  );
};

export default COSOCompliancePage;
